import { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useForm } from 'react-hook-form';
import { SubmittedReflectionFormValues, WeatherConstraint } from './types';
import WeatherReflectionConstraints from './WeatherReflectionConstraints';
import {
  convertNumberFromDB,
  convertToImperial,
} from 'features/weather/utils/dataConverters';
import { MeasurementData } from 'features/weather/types';
import { grey } from '@mui/material/colors';
import { useFormatForecastData } from 'features/forecasts/hooks/useFormatForecastData';
import { HourlyData } from 'types/models/ForecastDto';
import {
  filterForecasts,
  getEnumDurationFromMonthDifference,
  getStartDateFromDurationEnum,
  getStringRepresentationOfConstraints,
} from './utils/formHelpers';
import { analyzerChanged } from 'features/analyzers/analyzerSlice';
import { useAPICall } from 'hooks/useAPICall';
import { getTraceHistorySample } from 'api/snapshotApi';
import { TraceHistorySample } from 'types';
import WeatherTraceDisplay from './WeatherTraceDisplay';
import ForecastReflectionFilters from './ForecastReflectionFilters';
import ForecastReflectionTabs from './ForecastReflectionTabs';
import ShareForecastButton from './ShareForecastButton';
import ForecastReflectionGenerationForm from './ForecastReflectionGenerationForm';
import { useParseQueryString } from 'features/traceHistory/hooks/useParseQueryString';
import { differenceInMonths } from 'date-fns';
import { useLocationByAnalyzer } from 'features/locations/api/getLocationByAnalyzer';
import { useDish } from 'features/dishes/api/getDish';
import { useTimestamps } from 'features/weather/api/getTimestamps';
import { convertConstraintsForAPICall } from './utils/constraintConverters';
import { zonedTimeToUtc } from 'date-fns-tz';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';

const ForecastReflectionPage = () => {
  const dispatch = useAppDispatch();
  const { timezone } = useUserDateTime();
  const { safeCall: safeGetTraceHistory } = useAPICall(getTraceHistorySample);
  const [traceIndex, setTraceIndex] = useState(0);
  const handleTraceIndexChanged = (value: any) => {
    if (!timeline) return;
    if (typeof value === 'number' && value < timeline.length) {
      setTraceIndex(value);
    }
  };
  const [editAlerts, setEditAlerts] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentTrace, setCurrentTrace] = useState<
    TraceHistorySample | undefined
  >(undefined);

  const inImperial = useAppSelector(
    (state) => state.auth.user?.us_customary_units
  );

  const {
    control,
    watch,
    getValues,
    handleSubmit,
    setValue,
    formState: { isSubmitted },
  } = useForm({
    defaultValues: {
      location_id: 0,
      dish_id: 0,
      duration: 2,
      analyzerId: -1,
      snapshotId: -1,
    },
  });
  const { data: dish } = useDish({
    dishId: watch('dish_id'),
  });
  const [hourlyForecasts, setHourlyForecasts] = useState<HourlyData[]>([]);
  const [weatherConstraints, setWeatherConstraints] = useState<
    WeatherConstraint[]
  >([
    {
      measurement: '',
      level: '0',
      greaterThan: true,
    },
  ]);

  const {
    hasQueryString,
    startTime,
    endTime,
    snapshotIdToLoad,
    analyzerIdToLoad,
    traceIndex: queryTraceIndex,
    weatherConstraints: queryWeatherConstraints,
    dishIdToLoad,
  } = useParseQueryString(isSubmitted);

  const { data: locationForAnalyzer } = useLocationByAnalyzer({
    analyzerId:
      watch('analyzerId') !== -1 ? watch('analyzerId') : analyzerIdToLoad,
  });

  const [submittedFormValues, setSubmittedFormValues] =
    useState<SubmittedReflectionFormValues>({
      analyzerId: -1,
      snapshotId: -1,
      constraints: {
        begin: zonedTimeToUtc(
          getStartDateFromDurationEnum(2),
          timezone
        ).toISOString(),
        end: zonedTimeToUtc(new Date(), timezone).toISOString(),
        constraints: [],
      },
    });

  const { isLoading, data: timeline } = useTimestamps({
    locationId: locationForAnalyzer?.id,
    snapshotId: submittedFormValues.snapshotId,
    constraints: {
      begin: submittedFormValues.constraints.begin,
      end: submittedFormValues.constraints.end,
      constraints: submittedFormValues.constraints.constraints,
    },
  });

  useEffect(() => {
    if (!timeline || !timeline[traceIndex] || !timeline[traceIndex].time)
      return;
    const fetchTraceSample = async () => {
      const sample = await safeGetTraceHistory(
        watch('snapshotId'),
        timeline[traceIndex].time
      );
      setCurrentTrace(sample);
    };
    fetchTraceSample();
  }, [watch, traceIndex, safeGetTraceHistory, timeline]);

  const { formattedForecast: convertedForecast } = useFormatForecastData(
    watch('location_id')
  );

  const getConstraints = () => {
    if (!dish) return;
    const newConstraints = dish.weatherAlerts.map((alert) => ({
      measurement: alert.condition,
      level:
        convertNumberFromDB(
          convertToImperial(
            alert.warning,
            alert.condition as MeasurementData,
            inImperial
          ),
          alert.condition as MeasurementData
        )?.toFixed(2) || '0',
      greaterThan: true,
    }));
    if (newConstraints.length > 0) setWeatherConstraints(newConstraints);
    else
      setWeatherConstraints([
        { measurement: '', level: '0', greaterThan: true },
      ]);
    setEditAlerts(true);
  };

  useEffect(() => {
    if (!editAlerts && !hasQueryString && timeline && timeline.length > 0)
      return;
    const filteredForecasts = filterForecasts(
      convertedForecast ? convertedForecast.hourlyForecast : [],
      weatherConstraints,
      inImperial
    );
    setHourlyForecasts(filteredForecasts);
  }, [
    convertedForecast,
    inImperial,
    hasQueryString,
    weatherConstraints,
    editAlerts,
    timeline,
  ]);

  const onSubmit = async () => {
    setEditAlerts(false);
    const formState = getValues();
    if (!formState.location_id) return;
    dispatch(analyzerChanged(formState.analyzerId));
    setSubmittedFormValues({
      analyzerId: formState.analyzerId,
      snapshotId: formState.snapshotId,
      constraints: {
        begin: zonedTimeToUtc(
          getStartDateFromDurationEnum(formState.duration),
          timezone
        ).toISOString(),
        end: zonedTimeToUtc(new Date(), timezone).toISOString(),
        constraints: convertConstraintsForAPICall(
          weatherConstraints,
          inImperial
        ),
      },
    });
    setTraceIndex(0);
    setSubmitted(true);
  };

  useEffect(() => {
    const initializePage = async () => {
      if (!locationForAnalyzer?.id || !hasQueryString || !startTime || !endTime)
        return;

      setValue(
        'location_id',
        locationForAnalyzer?.id ? locationForAnalyzer.id : 0
      );
      setValue('dish_id', dishIdToLoad === -1 ? 0 : dishIdToLoad);
      setValue('analyzerId', analyzerIdToLoad);
      setValue('snapshotId', snapshotIdToLoad);
      setValue(
        'duration',
        getEnumDurationFromMonthDifference(
          differenceInMonths(new Date(endTime), new Date(startTime))
        )
      );
      if (queryWeatherConstraints.length > 0)
        setWeatherConstraints(
          queryWeatherConstraints.map((constraint) => ({
            ...constraint,
            level:
              // @ts-ignore
              convertNumberFromDB(
                convertToImperial(
                  Number(constraint.level),
                  constraint.measurement as MeasurementData,
                  inImperial
                ),
                constraint.measurement as MeasurementData
              ).toFixed(2),
          }))
        );
      else
        setWeatherConstraints([
          {
            measurement: '',
            level: '0',
            greaterThan: true,
          },
        ]);
      dispatch(analyzerChanged(analyzerIdToLoad));
      setSubmittedFormValues({
        analyzerId: analyzerIdToLoad,
        snapshotId: snapshotIdToLoad,
        constraints: {
          begin: startTime,
          end: endTime,
          constraints: queryWeatherConstraints,
        },
      });
      setTraceIndex(queryTraceIndex);
      setSubmitted(true);
    };
    initializePage();
  }, [
    dishIdToLoad,
    safeGetTraceHistory,
    queryWeatherConstraints,
    inImperial,
    setValue,
    analyzerIdToLoad,
    dispatch,
    endTime,
    hasQueryString,
    locationForAnalyzer?.id,
    snapshotIdToLoad,
    startTime,
    queryTraceIndex,
  ]);

  return (
    <Grid container direction='column' spacing={2}>
      <Grid container item alignItems='center' spacing={1}>
        <ForecastReflectionFilters
          control={control}
          watch={watch}
          getConstraints={getConstraints}
        />
        <ForecastReflectionGenerationForm
          control={control}
          watch={watch}
          handleSubmit={handleSubmit(onSubmit)}
          generateDisabled={
            !watch('analyzerId') ||
            !watch('snapshotId') ||
            watch('snapshotId') === -1 ||
            watch('analyzerId') === -1
          }
        />
        <ShareForecastButton
          watch={watch}
          traceIndex={traceIndex}
          weatherConstraints={weatherConstraints}
          readyToShare={!!(timeline && timeline.length > 0)}
        />
      </Grid>
      <Grid container item wrap='nowrap'>
        <Grid container item xs={6}>
          <Grid container item component={Paper} direction='column'>
            {editAlerts ? (
              <>
                <Grid
                  container
                  item
                  alignItems='center'
                  justifyContent='space-between'
                >
                  <Typography color={grey[500]} sx={{ p: '1rem' }}>
                    Edit alerts to compare against forecasts
                  </Typography>
                </Grid>
                <WeatherReflectionConstraints
                  weatherConstraints={weatherConstraints}
                  setWeatherConstraints={setWeatherConstraints}
                  hide={false}
                />
              </>
            ) : isLoading ? (
              <Grid
                item
                style={{ height: '40vh' }}
                container
                justifyContent='center'
                alignItems='center'
                direction='column'
              >
                <CircularProgress />
                <Typography sx={{ p: '2rem' }} color={grey[500]}>
                  If you've selected multiple months of history, this could take
                  a few seconds...
                </Typography>
              </Grid>
            ) : submitted ? (
              <Grid item container direction='column'>
                <Grid
                  item
                  container
                  style={{ width: '100%', padding: '.5rem', paddingRight: 0 }}
                  justifyContent='space-between'
                  alignItems='center'
                  spacing={1}
                >
                  <Grid item style={{ marginLeft: '.5rem' }}>
                    <Typography>{`Alert Filters: ${getStringRepresentationOfConstraints(
                      weatherConstraints,
                      inImperial
                    )}`}</Typography>
                  </Grid>
                  <Grid item>
                    <Button
                      variant='contained'
                      color='secondary'
                      onClick={() => setEditAlerts(true)}
                    >
                      Modify Alerts
                    </Button>
                  </Grid>
                </Grid>
                {timeline && (
                  <WeatherTraceDisplay
                    analyzerId={submittedFormValues.analyzerId}
                    snapshotId={submittedFormValues.snapshotId}
                    traceSample={currentTrace}
                    traceIndex={traceIndex}
                    timeline={timeline}
                    handleTraceIndexChanged={handleTraceIndexChanged}
                  />
                )}
              </Grid>
            ) : (
              <Grid
                item
                style={{ height: '40vh' }}
                container
                justifyContent='center'
                alignItems='center'
                direction='column'
              >
                <Typography sx={{ p: '2rem' }} color={grey[500]}>
                  Select a location and dish to view alerts, or click Get Alerts
                  to begin configuring your own alert filters
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid container item xs>
          <Grid container item component={Paper} direction='column'>
            <ForecastReflectionTabs
              watch={watch}
              isSubmitted={submitted}
              hourlyForecasts={hourlyForecasts}
              currentTrace={currentTrace}
              editAlerts={editAlerts}
              noTraces={!timeline || timeline.length === 0}
              // @ts-ignore
              weatherConstraints={weatherConstraints}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ForecastReflectionPage;
